import * as React from "react";
import { AlertCircle } from "lucide-react";

import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/hooks/useTranslation";
import { localizeServerText } from "@shared/serverText";

/**
 * One form field — label · control · hint · error — for the forms that do not
 * use react-hook-form (ui/form.tsx is that one's twin).
 *
 * What it is FOR is the wiring, which hand-built fields keep getting wrong:
 *
 *  - the label's `htmlFor` and the control's `id` are the same generated id,
 *    so clicking the label focuses the control and the control has a name;
 *  - the hint and the error are `aria-describedby` on the control, so a screen
 *    reader hears "Email, edit text, We'll send the receipt here" and, after a
 *    failed save, "…, Enter an email address like name@company.com";
 *  - `aria-invalid` is set from `error`, which is what the controls' own error
 *    styling keys on (Input, Textarea, DatePicker: border AND ring, so the
 *    state is not colour alone);
 *  - `required` marks the label with a visible asterisk and the control with
 *    `aria-required` — the asterisk itself is hidden from assistive tech,
 *    which would otherwise read "Email star".
 *
 * Two ways to give it the control:
 *
 *   <Field label="Email" hint="…" error={errors.email}>
 *     <Input value={email} onChange={…} />
 *   </Field>
 *
 * — the single child is cloned with the id and aria props (its own `id` and
 * `aria-describedby`, if it has them, are kept and joined). Or, for a control
 * whose TYPE wants the id up front (DatePicker's day API refuses to compile
 * unnamed), take them from a function:
 *
 *   <Field label="Closing date" error={errors.closing}>
 *     {(control) => <DatePicker {...control} value={day} onChange={setDay} />}
 *   </Field>
 *
 * VALIDATION TIMING is the caller's, and the rule is: validate on Save, tidy
 * on blur (hooks/use-save-validation.ts, lib/fieldFormat.ts). An error that
 * appears while someone is still typing is an error about a value they have
 * not finished — see that hook for the whole argument.
 */

export interface FieldControlProps {
  id: string;
  "aria-describedby"?: string;
  "aria-invalid"?: true;
  "aria-required"?: true;
}

export interface FieldProps {
  label: React.ReactNode;
  hint?: React.ReactNode;
  /** The message after a failed save. Falsy for none. */
  error?: React.ReactNode;
  required?: boolean;
  /** Use this id instead of a generated one (e.g. a test or an anchor needs it). */
  id?: string;
  /** Visually hide the label; it still names the control. */
  hideLabel?: boolean;
  className?: string;
  labelClassName?: string;
  children: React.ReactElement | ((control: FieldControlProps) => React.ReactNode);
}

const FieldContext = React.createContext<FieldControlProps | null>(null);

/** The control props of the enclosing `<Field>`, for a control built deeper down. */
export function useField(): FieldControlProps | null {
  return React.useContext(FieldContext);
}

export function Field({
  label,
  hint,
  error,
  required,
  id: idProp,
  hideLabel,
  className,
  labelClassName,
  children,
}: FieldProps) {
  const t = useTranslation();
  const auto = React.useId();
  const childProps =
    typeof children === "function" ? null : ((children as React.ReactElement).props as Record<string, unknown>);
  const id = idProp ?? (typeof childProps?.id === "string" ? (childProps.id as string) : `${auto}-control`);
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const hasError = Boolean(error);

  const describedBy =
    [
      typeof childProps?.["aria-describedby"] === "string" ? (childProps["aria-describedby"] as string) : undefined,
      hint ? hintId : undefined,
      hasError ? errorId : undefined,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

  const control: FieldControlProps = {
    id,
    ...(describedBy ? { "aria-describedby": describedBy } : {}),
    ...(hasError ? { "aria-invalid": true as const } : {}),
    ...(required ? { "aria-required": true as const } : {}),
  };

  return (
    <FieldContext.Provider value={control}>
      <div className={cn("space-y-1.5", className)} data-invalid={hasError || undefined}>
        {/* `block`: a <label> is inline, and beside an inline-flex control
            (a stepper, a switch) it would share the control's line. */}
        <Label htmlFor={id} className={cn("block w-fit", hideLabel && "sr-only", labelClassName)}>
          {label}
          {required && (
            <span className="text-foreground" aria-hidden="true">
              {" *"}
            </span>
          )}
        </Label>
        {typeof children === "function" ? children(control) : React.cloneElement(children, control)}
        {hint && (
          <p id={hintId} className="text-caption text-muted-foreground">
            {hint}
          </p>
        )}
        {hasError && (
          <p id={errorId} className="flex items-start gap-1.5 text-caption font-semibold text-destructive">
            <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden="true" />
            {/* A server's sentence (a field error from the API) in the reader's language. */}
            <span>{typeof error === "string" ? localizeServerText(error, t) : error}</span>
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}

/**
 * Warn, in development, when a control reaches the screen with no name.
 *
 * Some controls cannot be made to REQUIRE a label in their types without
 * breaking every existing call site (a Switch inside `<FormControl>` gets its
 * id at runtime from a Slot, invisible to the type checker). This is the
 * runtime half: after mount it asks the element whether anything names it —
 * `aria-label`, `aria-labelledby`, a `<label for>` (the element's own
 * `.labels`), or a wrapping `<label>` — and logs the element if not. Nothing
 * ships: the check is compiled out of a production build.
 */
export function useWarnIfUnnamed(ref: React.RefObject<HTMLElement | null>, what: string) {
  React.useEffect(() => {
    if (!import.meta.env?.DEV) return;
    const el = ref.current;
    if (!el) return;
    // A frame later, so a <label htmlFor> rendered after this control is in the DOM.
    const t = window.setTimeout(() => {
      if (!el.isConnected) return;
      const named =
        Boolean(el.getAttribute("aria-label")?.trim()) ||
        Boolean(el.getAttribute("aria-labelledby")) ||
        Boolean((el as HTMLButtonElement).labels?.length) ||
        Boolean(el.closest("label"));
      if (!named) {
        // eslint-disable-next-line no-console
        console.warn(`[a11y] ${what} has no accessible name — give it a label, aria-label, or a <Label htmlFor>.`, el);
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [ref, what]);
}
